// SPDX-License-Identifier: AGPL-3.0-or-later

import {MessageFlags} from '@fluxer/constants/src/ChannelConstants';
import {
	ButtonStyles,
	MESSAGE_COMPONENT_LIMITS as LIMITS,
	MESSAGE_COMPONENT_REPLY_CODES,
	SelectDefaultValueTypes,
	SeparatorSpacing,
	TextInputStyles,
	MessageComponentTypes as Types,
} from '@fluxer/constants/src/MessageComponentConstants';
import {
	ApplicationCommandSnowflake,
	ApplicationCommandSnowflakeInput,
} from '@fluxer/schema/src/domains/application_command/ApplicationCommandSchemas';
import {InteractionInvokeResponse} from '@fluxer/schema/src/domains/application_command/InteractionStatusSchemas';
import {createStringType, SnowflakeStringType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {AttachmentURLType, URLType} from '@fluxer/schema/src/primitives/UrlValidators';
import {z} from 'zod';

// custom_id and select values go back to the bot byte for byte, so they are bounded but never normalized.
export const ComponentCustomId = z.string().min(1).max(LIMITS.customIdMax);
const CustomId = ComponentCustomId;
const SelectValue = z.string().min(1).max(LIMITS.selectOptionTextMax);

function literals<const T extends ReadonlyArray<number>>(values: T) {
	return z.union(values.map((value) => z.literal(value)) as unknown as [z.ZodLiteral<T[number]>]);
}

const EmojiRequest = z
	.strictObject({
		id: ApplicationCommandSnowflakeInput.nullish(),
		name: z.string().min(1).max(64).nullish(),
		animated: z.boolean().nullish(),
	})
	.refine((emoji) => emoji.id != null || emoji.name != null, 'An emoji needs an id or a name');

const LinkUrl = URLType.refine((url) => url.length <= LIMITS.linkUrlMax, 'Link URL is too long');

const ButtonRequest = z
	.strictObject({
		type: z.literal(Types.BUTTON),
		style: literals(Object.values(ButtonStyles)),
		label: createStringType(1, LIMITS.labelMax).nullish(),
		emoji: EmojiRequest.nullish(),
		custom_id: CustomId.nullish(),
		url: LinkUrl.nullish(),
		disabled: z.boolean().nullish(),
	})
	.superRefine((button, ctx) => {
		const link = button.style === ButtonStyles.LINK;
		if (link ? button.url == null || button.custom_id != null : button.custom_id == null || button.url != null)
			ctx.addIssue({code: 'custom', message: 'Link buttons take a url, other buttons take a custom_id'});
		if (button.label == null && button.emoji == null)
			ctx.addIssue({code: 'custom', message: 'A button needs a label or an emoji'});
	});

const SelectFields = {
	custom_id: CustomId,
	placeholder: createStringType(1, LIMITS.placeholderMax).nullish(),
	min_values: z.number().int().min(0).max(LIMITS.selectValuesMax).nullish(),
	max_values: z.number().int().min(1).max(LIMITS.selectValuesMax).nullish(),
	disabled: z.boolean().nullish(),
	// discord.py sends it on every select; it only means something inside a modal.
	required: z.boolean().nullish(),
};

interface SelectBounds {
	min_values?: number | null;
	max_values?: number | null;
}

function checkSelectCount(select: SelectBounds, available: number | null, chosen: number, ctx: z.RefinementCtx) {
	const min = select.min_values ?? 1;
	const max = select.max_values ?? 1;
	if (min > max) ctx.addIssue({code: 'custom', message: 'min_values cannot exceed max_values'});
	if (available !== null && max > available)
		ctx.addIssue({code: 'custom', message: 'max_values cannot exceed the number of options'});
	if (chosen > max) ctx.addIssue({code: 'custom', message: 'Too many default values'});
}

const SelectOptionRequest = z.strictObject({
	label: createStringType(1, LIMITS.selectOptionTextMax),
	value: SelectValue,
	description: createStringType(1, LIMITS.selectOptionTextMax).nullish(),
	emoji: EmojiRequest.nullish(),
	default: z.boolean().nullish(),
});

const StringSelectRequest = z
	.strictObject({
		...SelectFields,
		type: z.literal(Types.STRING_SELECT),
		options: z.array(SelectOptionRequest).min(1).max(LIMITS.selectOptionsMax),
	})
	.superRefine((select, ctx) => {
		if (new Set(select.options.map(({value}) => value)).size !== select.options.length)
			ctx.addIssue({code: 'custom', message: 'Option values must be unique'});
		checkSelectCount(select, select.options.length, select.options.filter((option) => option.default).length, ctx);
	});

type DefaultValueType = (typeof SelectDefaultValueTypes)[keyof typeof SelectDefaultValueTypes];

function autoSelect<const K extends number>(type: K, allowed: ReadonlyArray<DefaultValueType>) {
	return z
		.strictObject({
			...SelectFields,
			type: z.literal(type),
			channel_types: z.array(z.number().int().min(0).max(255)).max(25).nullish(),
			default_values: z
				.array(z.strictObject({id: ApplicationCommandSnowflakeInput, type: z.enum(allowed as [DefaultValueType])}))
				.max(LIMITS.selectValuesMax)
				.nullish(),
		})
		.superRefine((select, ctx) => {
			if (select.channel_types != null && select.type !== Types.CHANNEL_SELECT)
				ctx.addIssue({code: 'custom', message: 'Only channel selects take channel_types'});
			checkSelectCount(select, null, select.default_values?.length ?? 0, ctx);
		});
}

const UserSelectRequest = autoSelect(Types.USER_SELECT, [SelectDefaultValueTypes.USER]);
const RoleSelectRequest = autoSelect(Types.ROLE_SELECT, [SelectDefaultValueTypes.ROLE]);
const MentionableSelectRequest = autoSelect(Types.MENTIONABLE_SELECT, [
	SelectDefaultValueTypes.USER,
	SelectDefaultValueTypes.ROLE,
]);
const ChannelSelectRequest = autoSelect(Types.CHANNEL_SELECT, [SelectDefaultValueTypes.CHANNEL]);

const ActionRowRequest = z
	.strictObject({
		type: z.literal(Types.ACTION_ROW),
		components: z
			.array(
				z.discriminatedUnion('type', [
					ButtonRequest,
					StringSelectRequest,
					UserSelectRequest,
					RoleSelectRequest,
					MentionableSelectRequest,
					ChannelSelectRequest,
				]),
			)
			.min(1)
			.max(LIMITS.buttonsPerRow),
	})
	.refine(
		(row) => row.components.length === 1 || row.components.every((child) => child.type === Types.BUTTON),
		'A select menu fills its row alone',
	);

const TextDisplayRequest = z.strictObject({
	type: z.literal(Types.TEXT_DISPLAY),
	content: createStringType(1, LIMITS.v2TextMax),
});
const MediaRequest = z.strictObject({url: AttachmentURLType});
const MediaDescription = createStringType(1, LIMITS.mediaDescriptionMax).nullish();
const ThumbnailRequest = z.strictObject({
	type: z.literal(Types.THUMBNAIL),
	media: MediaRequest,
	description: MediaDescription,
	spoiler: z.boolean().nullish(),
});
const SectionRequest = z.strictObject({
	type: z.literal(Types.SECTION),
	components: z.array(TextDisplayRequest).min(1).max(LIMITS.sectionTextsMax),
	accessory: z.discriminatedUnion('type', [ButtonRequest, ThumbnailRequest]),
});
const MediaGalleryRequest = z.strictObject({
	type: z.literal(Types.MEDIA_GALLERY),
	items: z
		.array(z.strictObject({media: MediaRequest, description: MediaDescription, spoiler: z.boolean().nullish()}))
		.min(1)
		.max(LIMITS.mediaGalleryItemsMax),
});
const FileRequest = z.strictObject({
	type: z.literal(Types.FILE),
	file: z.strictObject({url: AttachmentURLType.refine((url) => url.startsWith('attachment://'), 'Use attachment://')}),
	spoiler: z.boolean().nullish(),
});
const SeparatorRequest = z.strictObject({
	type: z.literal(Types.SEPARATOR),
	divider: z.boolean().nullish(),
	spacing: literals(Object.values(SeparatorSpacing)).nullish(),
});
const ContainerChildRequest = z.discriminatedUnion('type', [
	ActionRowRequest,
	SectionRequest,
	TextDisplayRequest,
	MediaGalleryRequest,
	FileRequest,
	SeparatorRequest,
]);
const ContainerRequest = z.strictObject({
	type: z.literal(Types.CONTAINER),
	components: z.array(ContainerChildRequest).min(1).max(LIMITS.v2ComponentsMax),
	accent_color: z.number().int().min(0).max(0xffffff).nullish(),
	spoiler: z.boolean().nullish(),
});

interface ComponentNode {
	type: number;
	custom_id?: string | null;
	content?: string;
	components?: ReadonlyArray<ComponentNode>;
	component?: ComponentNode;
	accessory?: ComponentNode;
}

function* walk(nodes: ReadonlyArray<ComponentNode>): Generator<ComponentNode> {
	for (const node of nodes) {
		yield node;
		if (node.components) yield* walk(node.components);
		if (node.component) yield* walk([node.component]);
		if (node.accessory) yield* walk([node.accessory]);
	}
}

function checkTree(nodes: ReadonlyArray<ComponentNode>, ctx: z.RefinementCtx, v2: boolean) {
	const all = [...walk(nodes)];
	const ids = all.flatMap((node) => (node.custom_id ? [node.custom_id] : []));
	if (new Set(ids).size !== ids.length) ctx.addIssue({code: 'custom', message: 'custom_id must be unique per message'});
	if (!v2) return;
	if (all.length > LIMITS.v2ComponentsMax) ctx.addIssue({code: 'custom', message: 'Too many components'});
	const text = all.reduce(
		(total, node) => total + (node.type === Types.TEXT_DISPLAY ? (node.content?.length ?? 0) : 0),
		0,
	);
	if (text > LIMITS.v2TextMax) ctx.addIssue({code: 'custom', message: 'Too much text'});
}

// Parsed only for a bot whose component keys are on; for everyone else the field is dropped unread.
export const MessageComponentsRequest = z
	.array(ActionRowRequest)
	.max(LIMITS.rows)
	.superRefine((rows, ctx) => checkTree(rows, ctx, false));
export type MessageComponentsRequest = z.infer<typeof MessageComponentsRequest>;

// The top level of a message flagged IS_COMPONENTS_V2.
export const MessageComponentsV2Request = z
	.array(z.discriminatedUnion('type', [...ContainerChildRequest.options, ContainerRequest]))
	.min(1)
	.max(LIMITS.v2ComponentsMax)
	.superRefine((nodes, ctx) => checkTree(nodes, ctx, true));
export type MessageComponentsV2Request = z.infer<typeof MessageComponentsV2Request>;

export function componentsSchemaForFlags(flags: number) {
	return (flags & MessageFlags.IS_COMPONENTS_V2) !== 0 ? MessageComponentsV2Request : MessageComponentsRequest;
}

const TextInputFields = {
	type: z.literal(Types.TEXT_INPUT),
	custom_id: CustomId,
	style: literals(Object.values(TextInputStyles)),
	min_length: z.number().int().min(0).max(LIMITS.textInputValueMax).nullish(),
	max_length: z.number().int().min(1).max(LIMITS.textInputValueMax).nullish(),
	required: z.boolean().nullish(),
	value: z.string().max(LIMITS.textInputValueMax).nullish(),
	placeholder: createStringType(1, LIMITS.textInputPlaceholderMax).nullish(),
};

interface TextInputBounds {
	min_length?: number | null;
	max_length?: number | null;
	value?: string | null;
}

function checkTextInput(input: TextInputBounds, ctx: z.RefinementCtx) {
	const max = input.max_length ?? LIMITS.textInputValueMax;
	if ((input.min_length ?? 0) > max) ctx.addIssue({code: 'custom', message: 'min_length cannot exceed max_length'});
	if ((input.value?.length ?? 0) > max) ctx.addIssue({code: 'custom', message: 'value exceeds max_length'});
}

// The Label carries the text; discord.py still writes label: null on the input inside it.
const LabelledTextInput = z
	.strictObject({...TextInputFields, label: z.null().optional()})
	.superRefine(checkTextInput)
	.transform(({label: _label, ...input}) => input);
const LegacyTextInput = z
	.strictObject({...TextInputFields, label: createStringType(1, LIMITS.textInputLabelMax)})
	.superRefine(checkTextInput);

// Selects and file uploads inside modals are not offered yet; they fail as unknown components.
export const ModalComponentsRequest = z
	.array(
		z.discriminatedUnion('type', [
			z.strictObject({
				type: z.literal(Types.LABEL),
				label: createStringType(1, LIMITS.textInputLabelMax),
				description: createStringType(1, LIMITS.labelDescriptionMax).nullish(),
				component: LabelledTextInput,
			}),
			z.strictObject({type: z.literal(Types.ACTION_ROW), components: z.tuple([LegacyTextInput])}),
			TextDisplayRequest,
		]),
	)
	.min(1)
	.max(LIMITS.modalComponentsMax)
	.superRefine((nodes, ctx) => checkTree(nodes, ctx, false));
export type ModalComponentsRequest = z.infer<typeof ModalComponentsRequest>;

const EmojiResponse = z.object({
	name: z.string().describe('The emoji name or unicode character'),
	id: SnowflakeStringType.nullable().describe('The custom emoji ID, or null for a unicode emoji'),
	animated: z.boolean().describe('Whether the custom emoji is animated'),
});

export const MessageButtonResponse = z.object({
	type: z.literal(Types.BUTTON).describe('Component type: 2 is a button'),
	style: z.number().int().min(1).max(5).describe('Button style: 1 primary, 2 secondary, 3 success, 4 danger, 5 link'),
	label: z.string().nullish().describe('The button text'),
	emoji: EmojiResponse.nullish().describe('The emoji shown on the button'),
	custom_id: z
		.string()
		.max(LIMITS.customIdMax)
		.nullish()
		.describe('The ID sent back when the button is clicked; absent on link buttons'),
	url: z.string().nullish().describe('Where a link button leads; open it only after the external link warning'),
	disabled: z.boolean().describe('Whether the button can be clicked'),
});

const SelectTypeResponse = literals([
	Types.STRING_SELECT,
	Types.USER_SELECT,
	Types.ROLE_SELECT,
	Types.MENTIONABLE_SELECT,
	Types.CHANNEL_SELECT,
]);

export const MessageSelectResponse = z.object({
	type: SelectTypeResponse.describe('Component type: 3 string, 5 user, 6 role, 7 mentionable, 8 channel select'),
	custom_id: z.string().max(LIMITS.customIdMax).describe('The ID sent back with the chosen values'),
	options: z
		.array(
			z.object({
				label: z.string(),
				value: z.string(),
				description: z.string().nullish(),
				emoji: EmojiResponse.nullish(),
				default: z.boolean().nullish(),
			}),
		)
		.nullish()
		.describe('The choices of a string select'),
	placeholder: z.string().nullish().describe('Text shown when nothing is chosen'),
	min_values: z.number().int().nullish().describe('Fewest values to choose'),
	max_values: z.number().int().nullish().describe('Most values to choose'),
	channel_types: z.array(z.number().int()).nullish().describe('Channel types a channel select offers'),
	default_values: z
		.array(z.object({id: SnowflakeStringType, type: z.enum(['user', 'role', 'channel'])}))
		.nullish()
		.describe('Values chosen in advance'),
	disabled: z.boolean().describe('Whether the menu can be used'),
});

export const MessageActionRowResponse = z.object({
	type: z.literal(Types.ACTION_ROW).describe('Component type: 1 is an action row'),
	components: z
		.array(z.union([MessageButtonResponse, MessageSelectResponse]))
		.max(LIMITS.buttonsPerRow)
		.describe('Up to five buttons or one select menu'),
});
export type MessageActionRow = z.infer<typeof MessageActionRowResponse>;

const MediaResponse = z.object({
	url: z.string().describe('The original URL or attachment reference'),
	proxy_url: z.string().nullish().describe('The media proxy URL clients load'),
	width: z.number().int().nullish(),
	height: z.number().int().nullish(),
	content_type: z.string().nullish(),
	placeholder: z.string().nullish(),
	flags: z.number().int().nullish(),
});
const TextDisplayResponse = z.object({type: z.literal(Types.TEXT_DISPLAY), content: z.string()});
const ThumbnailResponse = z.object({
	type: z.literal(Types.THUMBNAIL),
	media: MediaResponse,
	description: z.string().nullish(),
	spoiler: z.boolean().nullish(),
});
const SectionResponse = z.object({
	type: z.literal(Types.SECTION),
	components: z.array(TextDisplayResponse),
	accessory: z.union([MessageButtonResponse, ThumbnailResponse]),
});
const MediaGalleryResponse = z.object({
	type: z.literal(Types.MEDIA_GALLERY),
	items: z.array(z.object({media: MediaResponse, description: z.string().nullish(), spoiler: z.boolean().nullish()})),
});
const FileResponse = z.object({type: z.literal(Types.FILE), file: MediaResponse, spoiler: z.boolean().nullish()});
const SeparatorResponse = z.object({
	type: z.literal(Types.SEPARATOR),
	divider: z.boolean().nullish(),
	spacing: z.number().int().nullish(),
});
const ContainerChildResponse = z.union([
	MessageActionRowResponse,
	SectionResponse,
	TextDisplayResponse,
	MediaGalleryResponse,
	FileResponse,
	SeparatorResponse,
]);
const ContainerResponse = z.object({
	type: z.literal(Types.CONTAINER),
	components: z.array(ContainerChildResponse),
	accent_color: z.number().int().nullish(),
	spoiler: z.boolean().nullish(),
});

// Action rows only, unless the message carries IS_COMPONENTS_V2. Clients skip a kind they do not know.
export const MessageComponentResponse = z.union([...ContainerChildResponse.options, ContainerResponse]);
export type MessageComponentResponse = z.infer<typeof MessageComponentResponse>;

export const MessageComponentClickRequest = z.object({
	custom_id: CustomId.describe('The custom_id of the clicked component'),
	values: z
		.array(SelectValue)
		.max(LIMITS.selectValuesMax)
		.refine((values) => new Set(values).size === values.length, 'Duplicate value')
		.optional()
		.describe('The chosen values of a select menu'),
	session_id: z.string().min(1).max(64).optional().describe('The gateway session that receives a private reply'),
	nonce: z.string().min(1).max(32).optional().describe('Client value that makes a retried click safe'),
	source_interaction_id: ApplicationCommandSnowflake.optional().describe(
		'For a component on a private interaction reply, the interaction that produced it',
	),
});

export const MessageComponentClickResponse = z.object({
	reply: z
		.object({
			code: z.enum(MESSAGE_COMPONENT_REPLY_CODES).describe('What happened; the client shows a translated message'),
			channel_id: SnowflakeStringType.nullish().describe('A channel the reply points to'),
		})
		.nullable()
		.describe('The first-party bot reply shown only to the clicker, or null'),
	interaction: InteractionInvokeResponse.nullish().describe(
		'For a third-party bot: the interaction sent to it; its answer arrives over the gateway',
	),
});
export type MessageComponentClickResponse = z.infer<typeof MessageComponentClickResponse>;
